import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"
import type { UserType } from "@/src/auth/types"
import type {
    FormSchemaAddUser,
    FormSchemaRolePermission,
} from "@/src/schemas/formSchemas"
import type { PaginationState, BaseStoreState } from "../types"
import { useAuthStore } from "./auth.slice"
import {
    getUsers,
    updateTableRole,
    deletePermission,
    fetchUserPermission,
    addUser,
    editUser,
    getManagerList,
} from "@/src/services/users"

// Zod schemas for validation
const AddUserSchema = z.object({
    fullName: z.string().min(1, "Applicant name is required"),
    email: z.string().min(1, "Email is required").email("Email must be a valid email address"),
    phoneNumber: z.string().min(1, "Phone number is required"),
    roles: z.any(),
    managerId: z.any(),
    isAiScreeningEnabled: z.boolean(),
})

const RolePermissionSchema = z.object({
    admin: z.any(),
    manager: z.any(),
    recruiter: z.any(),
})

// Store state interface
export interface UserManagementStoreState extends BaseStoreState {
    users: UserType[]
    currentUser: UserType | null
    managerList: any[]
    permissions: any[]
    pagination: PaginationState
    searchQuery: string
    tabActive: string
}

// Store actions interface
export interface UserManagementStoreActions {
    // User management
    fetchUsers: (companyId: string, page: number, size: number, query?: string) => Promise<void>
    setCurrentUser: (id: string) => void
    addUser: (data: FormSchemaAddUser) => Promise<void>
    updateUser: (userId: string, data: FormSchemaAddUser) => Promise<void>
    deleteUser: (userId: string) => Promise<void>

    // Role and permission management
    updateTableRole: (data: FormSchemaRolePermission) => Promise<void>
    fetchUserPermission: () => Promise<void>
    deletePermission: (permissionId: string) => Promise<void>

    // Manager management
    fetchManagerList: (role: string) => Promise<void>

    // UI state
    setPagination: (pagination: Partial<PaginationState>) => void
    setSearchQuery: (query: string) => void
    setTabActive: (tab: string) => void
}

export type UserManagementStore = UserManagementStoreState & UserManagementStoreActions

export const useUserManagementStore = create<UserManagementStore>()(
    immer((set, get) => ({
            // State
            users: [],
            currentUser: null,
            managerList: [],
            permissions: [],
            pagination: { page: 0, pageSize: 40 },
            searchQuery: "",
            tabActive: "users",
            loading: false,
            error: null,

            // Actions
            fetchUsers: async (companyId: string, page: number, size: number, query = "") => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    if (!companyId) {
                        throw new Error("Company ID is required")
                    }

                    const response = await getUsers(companyId, page, size, query)

                    if (response?.usersStatus === "SUCCESS") {
                        set((state) => {
                            state.users = response.users
                            state.loading = false
                            state.error = null
                        })
                    } else {
                        throw new Error("Failed to fetch users")
                    }
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to fetch users"
                    })
                }
            },

            setCurrentUser: (id: string) => {
                const { users } = get()
                const user = users.find((u) => u.id === id)

                set((state) => {
                    state.currentUser = user || null
                })
            },

            addUser: async (data: FormSchemaAddUser) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // User data validation can be added later if needed
                    const response = await addUser(data)

                    if (response.status === 200) {
                        set((state) => {
                            state.loading = false
                            state.error = null
                        })

                        // Refresh users list
                        const { user } = useAuthStore.getState()
                        if (user?.activeWorkspace?.id) {
                            await get().fetchUsers(user.activeWorkspace.id, 0, 40, get().searchQuery)
                        }
                    } else {
                        throw new Error("Failed to add user")
                    }
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to add user"
                    })
                    throw error
                }
            },

            updateUser: async (userId: string, data: FormSchemaAddUser) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // User data validation can be added later if needed
                    const response = await editUser(data, userId)

                    if (response.status === 200) {
                        set((state) => {
                            state.loading = false
                            state.error = null
                        })

                        // Refresh users list
                        const { user } = useAuthStore.getState()
                        if (user?.activeWorkspace?.id) {
                            await get().fetchUsers(user.activeWorkspace.id, 0, 40, get().searchQuery)
                        }
                    } else {
                        throw new Error("Failed to update user")
                    }
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to update user"
                    })
                    throw error
                }
            },

            deleteUser: async (userId: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // This would typically call an API to delete user
                    // For now, we'll remove from local state

                    set((state) => {
                        state.users = state.users.filter((u) => u.id !== userId)
                        if (state.currentUser?.id === userId) {
                            state.currentUser = null
                        }
                        state.loading = false
                        state.error = null
                    })
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to delete user"
                    })
                    throw error
                }
            },

            updateTableRole: async (data: FormSchemaRolePermission) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    // Role permission data validation can be added later if needed
                    const response = await updateTableRole(data)

                    set((state) => {
                        state.loading = false
                        state.error = null
                    })

                    return response
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to update role"
                    })
                    throw error
                }
            },

            fetchUserPermission: async () => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const response = await fetchUserPermission()

                    if (response?.rolePermissionStatus === "SUCCESS") {
                        set((state) => {
                            state.permissions = response.rolePermission
                            state.loading = false
                            state.error = null
                        })
                    } else {
                        throw new Error("Failed to fetch user permissions")
                    }
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to fetch user permissions"
                    })
                }
            },

            deletePermission: async (permissionId: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const response = await deletePermission(permissionId)

                    set((state) => {
                        state.loading = false
                        state.error = null
                    })

                    // Refresh permissions
                    await get().fetchUserPermission()

                    return response
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to delete permission"
                    })
                    throw error
                }
            },

            fetchManagerList: async (role: string) => {
                set((state) => {
                    state.loading = true
                    state.error = null
                })

                try {
                    const response = await getManagerList(role)

                    if (response.managerListStatus === "SUCCESS") {
                        set((state) => {
                            state.managerList = response.managerList.content
                            state.loading = false
                            state.error = null
                        })
                    } else {
                        throw new Error("Failed to fetch manager list")
                    }
                } catch (error) {
                    set((state) => {
                        state.loading = false
                        state.error = error instanceof Error ? error.message : "Failed to fetch manager list"
                    })
                }
            },

            setPagination: (pagination: Partial<PaginationState>) => {
                set((state) => {
                    state.pagination = { ...state.pagination, ...pagination }
                })
            },

            setSearchQuery: (query: string) => {
                set((state) => {
                    state.searchQuery = query
                })
            },

            setTabActive: (tab: string) => {
                set((state) => {
                    state.tabActive = tab
                })
            },

        }))
)
